import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Save, Eye, Upload, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import { projectId, publicAnonKey } from "../utils/supabase/info";

interface EmailSignature {
  userId: string;
  fullName: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  website: string;
  logoUrl: string;
  photoUrl: string;
  includePhoto: boolean;
  includeLogo: boolean;
  customHtml: string;
  useCustomHtml: boolean;
  updatedAt: string;
}

interface EmailSignatureEditorProps {
  accessToken: string;
}

export function EmailSignatureEditor({ accessToken }: EmailSignatureEditorProps) {
  const [signature, setSignature] = useState<Partial<EmailSignature>>({
    fullName: "",
    title: "",
    company: "",
    email: "",
    phone: "",
    website: "",
    logoUrl: "",
    photoUrl: "",
    includePhoto: false,
    includeLogo: false,
    customHtml: "",
    useCustomHtml: false,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  useEffect(() => {
    loadSignature();
  }, []);

  const loadSignature = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-signature`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.signature) {
          setSignature(data.signature);
        }
      }
    } catch (error) {
      console.error("Error loading signature:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveSignature = async () => {
    setSaving(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/email-signature`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(signature),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSignature(data.signature);
        alert("Signature saved");
      } else {
        alert("Failed to save signature");
      }
    } catch (error) {
      console.error("Error saving signature:", error);
      alert("Error saving signature");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file: File, type: 'photo' | 'logo') => {
    const setUploading = type === 'photo' ? setUploadingPhoto : setUploadingLogo;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/upload/signature-image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (type === 'photo') {
          setSignature({ ...signature, photoUrl: data.url });
        } else {
          setSignature({ ...signature, logoUrl: data.url });
        }
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Upload failed");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Error uploading image");
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'photo' | 'logo') => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size is 5MB");
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert("Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed");
        return;
      }

      handleImageUpload(file, type);
    }
  };

  const generatePreviewHtml = () => {
    if (signature.useCustomHtml && signature.customHtml) {
      return signature.customHtml;
    }

    const parts: string[] = [];
    parts.push('<div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">');
    parts.push('<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, sans-serif; font-size: 14px; color: #333;">');
    parts.push('<tr>');

    if (signature.includePhoto && signature.photoUrl) {
      parts.push('<td style="padding-right: 20px; vertical-align: top;">');
      parts.push(`<img src="${signature.photoUrl}" alt="${signature.fullName}" style="width: 80px; height: 80px; border-radius: 50%; object-fit: cover;">`);
      parts.push('</td>');
    }

    parts.push('<td style="vertical-align: top;">');

    if (signature.fullName) {
      parts.push(`<div style="font-size: 16px; font-weight: bold; color: #111827; margin-bottom: 4px;">${signature.fullName}</div>`);
    }

    if (signature.title) {
      parts.push(`<div style="font-size: 14px; color: #6b7280; margin-bottom: 8px;">${signature.title}</div>`);
    }

    if (signature.company) {
      parts.push(`<div style="font-size: 13px; color: #374151; margin-bottom: 8px;">${signature.company}</div>`);
    }

    const contactParts: string[] = [];
    if (signature.email) {
      contactParts.push(`<a href="mailto:${signature.email}" style="color: #3b82f6; text-decoration: none;">${signature.email}</a>`);
    }
    if (signature.phone) {
      contactParts.push(`<a href="tel:${signature.phone}" style="color: #3b82f6; text-decoration: none;">${signature.phone}</a>`);
    }
    if (signature.website) {
      const websiteUrl = signature.website.startsWith('http') ? signature.website : `https://${signature.website}`;
      contactParts.push(`<a href="${websiteUrl}" style="color: #3b82f6; text-decoration: none;">${signature.website}</a>`);
    }

    if (contactParts.length > 0) {
      parts.push(`<div style="font-size: 13px; color: #6b7280; line-height: 1.8;">${contactParts.join(' • ')}</div>`);
    }

    if (signature.includeLogo && signature.logoUrl) {
      parts.push('<div style="margin-top: 12px;">');
      parts.push(`<img src="${signature.logoUrl}" alt="Company Logo" style="max-width: 150px; max-height: 50px;">`);
      parts.push('</div>');
    }

    parts.push('</td>');
    parts.push('</tr>');
    parts.push('</table>');
    parts.push('</div>');

    return parts.join('\n');
  };

  if (loading) {
    return <div className="p-4">Loading signature...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email Signature</CardTitle>
        <CardDescription>
          Customize your email signature that will be automatically included in invitation emails
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="structured">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="structured">Structured</TabsTrigger>
            <TabsTrigger value="custom">Custom HTML</TabsTrigger>
          </TabsList>

          <TabsContent value="structured" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={signature.fullName || ""}
                  onChange={(e) => setSignature({ ...signature, fullName: e.target.value })}
                  placeholder="John Smith"
                />
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={signature.title || ""}
                  onChange={(e) => setSignature({ ...signature, title: e.target.value })}
                  placeholder="Senior Consultant"
                />
              </div>

              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={signature.company || ""}
                  onChange={(e) => setSignature({ ...signature, company: e.target.value })}
                  placeholder="Acme Consulting"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={signature.email || ""}
                  onChange={(e) => setSignature({ ...signature, email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={signature.phone || ""}
                  onChange={(e) => setSignature({ ...signature, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={signature.website || ""}
                  onChange={(e) => setSignature({ ...signature, website: e.target.value })}
                  placeholder="www.example.com"
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="includePhoto">Include Profile Photo</Label>
                  <p className="text-sm text-muted-foreground">Show your photo in the signature</p>
                </div>
                <Switch
                  id="includePhoto"
                  checked={signature.includePhoto || false}
                  onCheckedChange={(checked) => setSignature({ ...signature, includePhoto: checked })}
                />
              </div>

              {signature.includePhoto && (
                <div className="space-y-2">
                  <Label htmlFor="photoUrl">Photo</Label>
                  {signature.photoUrl && (
                    <div className="flex items-center gap-4 mb-2">
                      <img 
                        src={signature.photoUrl} 
                        alt="Profile" 
                        className="h-20 w-20 rounded-full object-cover border-2"
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => document.getElementById('photoUpload')?.click()}
                      disabled={uploadingPhoto}
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Photo
                        </>
                      )}
                    </Button>
                    <input
                      id="photoUpload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e, 'photo')}
                    />
                  </div>
                  <Input
                    id="photoUrl"
                    value={signature.photoUrl || ""}
                    onChange={(e) => setSignature({ ...signature, photoUrl: e.target.value })}
                    placeholder="https://example.com/photo.jpg"
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload an image or paste a URL (max 5MB)
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="includeLogo">Include Company Logo</Label>
                  <p className="text-sm text-muted-foreground">Show your company logo</p>
                </div>
                <Switch
                  id="includeLogo"
                  checked={signature.includeLogo || false}
                  onCheckedChange={(checked) => setSignature({ ...signature, includeLogo: checked })}
                />
              </div>

              {signature.includeLogo && (
                <div className="space-y-2">
                  <Label htmlFor="logoUrl">Company Logo</Label>
                  {signature.logoUrl && (
                    <div className="flex items-center gap-4 mb-2">
                      <img 
                        src={signature.logoUrl} 
                        alt="Company Logo" 
                        className="h-12 max-w-[150px] object-contain border rounded p-2"
                      />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => document.getElementById('logoUpload')?.click()}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Logo
                        </>
                      )}
                    </Button>
                    <input
                      id="logoUpload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleFileSelect(e, 'logo')}
                    />
                  </div>
                  <Input
                    id="logoUrl"
                    value={signature.logoUrl || ""}
                    onChange={(e) => setSignature({ ...signature, logoUrl: e.target.value })}
                    placeholder="https://example.com/logo.png"
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground">
                    Upload an image or paste a URL (max 5MB)
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="custom" className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label htmlFor="useCustomHtml">Use Custom HTML</Label>
                <p className="text-sm text-muted-foreground">Override structured signature with custom HTML</p>
              </div>
              <Switch
                id="useCustomHtml"
                checked={signature.useCustomHtml || false}
                onCheckedChange={(checked) => setSignature({ ...signature, useCustomHtml: checked })}
              />
            </div>

            <div>
              <Label htmlFor="customHtml">Custom HTML Signature</Label>
              <Textarea
                id="customHtml"
                value={signature.customHtml || ""}
                onChange={(e) => setSignature({ ...signature, customHtml: e.target.value })}
                placeholder="<div>Your custom HTML signature...</div>"
                rows={12}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Write your own HTML for complete control over the signature appearance
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-2 mt-6">
          <Button onClick={saveSignature} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Signature"}
          </Button>
          <Button variant="outline" onClick={() => setShowPreview(!showPreview)}>
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? "Hide" : "Show"} Preview
          </Button>
        </div>

        {showPreview && (
          <div className="mt-6 p-4 border rounded-md bg-muted/50">
            <h3 className="font-medium mb-4">Preview:</h3>
            <div 
              className="bg-white p-6 rounded border"
              dangerouslySetInnerHTML={{ __html: generatePreviewHtml() }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}